import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import AddAlertIcon from '@material-ui/icons/AddAlert';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import IconButton from '@material-ui/core/IconButton';
import DataTable from '../../../../components/DataTable/DataTable';
import { serializeObjectToUrl } from '../../../../utils/urlHelper';
import { useGetAllProducts } from '../../hooks/api/productsApi';
import { useTheme } from '@material-ui/core/styles';
import { PriceTimer } from '../../../../components/PriceTimer/PriceTimer';
import { priceSources } from '../../../../constants/priceSources';
import { useHistory } from 'react-router';
import RefreshIcon from '@material-ui/icons/Refresh';
import userRoles from '../../../../constants/userRoles';
import useAppContext from '../../../../hooks/context/useAppContext';
import { commodities } from '../../../../constants/commodities';
import SmallDevelopmentChart from './SmallDevelopmentChart';

const AvailableProductsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  color: ${(props) => props.theme.custom.dataTable.header.color};
`;

const StyledToggleButton = styled(ToggleButton)`
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  padding: 0 20px;

  @media all and (width <= 500px) {
    font-size: 1 rem;
    padding: 0 5px;
  }
`;

const AvailableProductsHeaderLabelIcon = styled.div`
  display: flex;
  align-items: center;
  margin: 0 10px 0 0;
`;

const AvailableProductsHeaderButtonsLabel = styled.div`
  padding: 0 5px;

  @media all and (width <= 500px) {
    display: none;
  }
`;

const AvailableProductsHeaderButtons = styled.div`
  display: inline-flex;
  align-items: center;
  overflow-x: auto;
  
  @media all and (width <= 500px) {
    padding-left: 5px;
  }
`;

const AvailableProductsControlsButtons = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${(props) => props.theme.custom.dataTable.header.color};
`;

const AvailableProductsHeaderLabel = styled.div`
  font-size: 1.7rem;
  display: inline-flex;
  align-items: center;
`;

const contractDetail = 'contract';
const contractDetailBuyTab = 'buy';

const getRemainingTime = (validTo) => {
  const remaining = Math.floor((new Date(validTo) - new Date()) / 1000);
  return remaining > 0 ? remaining : 0;
};

const getTimeLine = (startDate, endDate, interval) => {
  const timeLine = [];

  let current = startDate.valueOf();
  const intervalEnd = endDate.valueOf();

  while (current <= intervalEnd) {
    timeLine.push(new Date(current).toISOString());
    current += interval;
  }

  return timeLine;
};

const getHistoricData = (timeline, apiData) => {
  const output = apiData?.reduce((result, item) => {
    const index = timeline.indexOf(new Date(item.validTo).toISOString());
    if (index !== -1) {
      result.push({
        x: index,
        y: item.price,
      });
    }
    return result;
  }, []);

  output.push({
    x: timeline.length - 1,
    y: null,
  });

  return output;
};


const getLabels = (dataType) => {
  const labels = {
    lastDay: 'app.products.lastDaysProgress',
    today: 'app.products.todayProgress',
  };
  let priceLabel;
  let progressLabel;
  if (dataType === priceSources.BESTASK) {
    priceLabel = <FormattedMessage id={'app.products.currentBestAskPrice'} />;
    progressLabel = <FormattedMessage id={labels.today} />;
  } else if (dataType === priceSources.BESTBID) {
    priceLabel = <FormattedMessage id={'app.products.currentBestBidPrice'} />;
    progressLabel = <FormattedMessage id={labels.today} />;
  } else {
    priceLabel = <FormattedMessage id={'app.products.settlementPrice'} />;
    progressLabel = <FormattedMessage id={labels.lastDay} />;
  }

  return {
    priceLabel,
    progressLabel,
  };
};

const getDevelopmentChartData = (priceSource, timeline, line) => {
  return priceSource !== 'SETTLEMENT' ? getHistoricData(timeline, line?.historicPrices) : line.historicPrices.map((historicPrice) => {
    return {
      y: historicPrice.price,
    };
  });
};

const priceAlertRedirect = (role, history) => {
  if (role === userRoles.CUSTOMER || role === userRoles.CUSTOMER_VIEWER) {
    history.push('/pricealert');
  }
};

const AvailableProducts = ({ type, years }) => {
  const { getAllProducts, error, loading, data } = useGetAllProducts();
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [year, setYear] = useState(null);
  const [yearList, setYearList] = useState([]);
  const history = useHistory();
  const theme = useTheme();
  const { user } = useAppContext();

  const commodity = commodities?.[type];

  const refreshData = useCallback(() => {
    if (year) {
      const query = {
        commodity: type,
        year: year,
        contractId: years[year],
      };
      (async () => {
        await getAllProducts(serializeObjectToUrl(query));
      })();
    }
  }, [year, type, getAllProducts, years]);

  const refreshControls = useMemo(
    () => (
      <AvailableProductsControlsButtons theme={theme}>
        <IconButton size={'small'} onClick={refreshData}>
          <RefreshIcon />
        </IconButton>
      </AvailableProductsControlsButtons>
    ),
    [refreshData, theme],
  );

  useEffect(() => {
    const yearsSet = Object.keys(years).filter(
      (yearValue) =>
        yearValue > new Date().getFullYear() ||
        (yearValue === new Date().getFullYear().toString() && new Date().getMonth() < 11));
    setYearList(yearsSet);
    setYear(yearsSet[0]);
  }, [years]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handleChange = (event, newYear) => {
    if (newYear !== null) {
      setYear(newYear);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTableData((previousState) =>
        previousState.map((item) => {
          const remainingTime = getRemainingTime(item.priceValidityRaw);
          return {
            ...item,
            priceValidity: <PriceTimer time={remainingTime} />,
            controls: remainingTime > 0 ? item.controls : refreshControls,
          };
        }),
      );
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, [getAllProducts, year, type, refreshControls]);

  useEffect(() => {
    if (data) {
      const startTime = new Date();
      startTime.setHours(10, 0, 0, 0);

      const endTime = new Date();
      endTime.setHours(18, 0, 0, 0);

      const timeline = getTimeLine(startTime, endTime, 1000 * 60);

      let dataType = priceSources.BESTASK;
      const output = data.map((line) => {
        const validTo = line?.price?.validTo;
        const remainingTime = getRemainingTime(validTo);
        const chartData = getDevelopmentChartData(line.priceSource.id, timeline, line);
        const controls =
          remainingTime > 0 ? (
            <AvailableProductsControlsButtons theme={theme}>
              <IconButton
                size={'small'}
                onClick={() => {
                  priceAlertRedirect(user.role, history);
                }}
              >
                {!line?.priceAlert?.isSet && !line?.priceAlert?.active && <AddAlertIcon style={{ color: '#aaaaaa' }} />}
                {line?.priceAlert?.isSet && !line?.priceAlert?.active && <NotificationsIcon />}
                {line?.priceAlert?.active && <NotificationsActiveIcon style={{ color: theme.custom.dataTable.warningColor }} />}
              </IconButton>
              <IconButton
                size={'small'}
                onClick={() => {
                  history.push(`/${contractDetail}/${line?.idContract}/${contractDetailBuyTab}`, { product: line.id });
                }}>
                <ShoppingCartIcon style={{ color: theme.palette.primary.main }} />
              </IconButton>
            </AvailableProductsControlsButtons>
          ) : (
            refreshControls
          );

        const { price, name, currency, priceSource } = line;
        dataType = priceSource?.id;

        return {
          product: name,
          // eslint-disable-next-line
          price: <FormattedNumber value={price?.price || 0} style="currency" currency={currency?.id} />,
          priceValidityRaw: validTo,
          priceValidity: <PriceTimer time={remainingTime} />,
          progress: <SmallDevelopmentChart chartData={chartData} />,
          controls: controls,
        };
      });

      const labels = getLabels(dataType);
      const fields = {
        product: {
          id: 'product',
          label: <FormattedMessage id={'app.products.product'} />,
          width: '32%',
          align: 'left',
        },
        price: {
          id: 'price',
          label: labels.priceLabel,
          width: '18%',
          align: 'right',
        },
        priceValidity: {
          id: 'priceValidity',
          label: <FormattedMessage id={'app.products.priceValidity'} />,
          width: '18%',
          align: 'right',
        },
        progress: {
          id: 'progress',
          label: labels.progressLabel,
          width: '20%',
        },
        controls: {
          id: 'controls',
          label: '',
        },
      };
      setColumns([fields.product, fields.price, fields.priceValidity, fields.progress, fields.controls]);
      setTableData(output);
    }
  }, [data, theme, history, refreshControls, user.role]);

  const icon = React.cloneElement(commodity.icon, { scale: 2 });

  return (
    <>
      <AvailableProductsHeader theme={theme}>
        <AvailableProductsHeaderLabel>
          <AvailableProductsHeaderLabelIcon>{icon}</AvailableProductsHeaderLabelIcon>
          <FormattedMessage id={commodity.title} />
        </AvailableProductsHeaderLabel>
        <AvailableProductsHeaderButtons>
          <AvailableProductsHeaderButtonsLabel>
            <FormattedMessage id={'app.products.contract'} />
          </AvailableProductsHeaderButtonsLabel>
          <ToggleButtonGroup size="small" exclusive value={year} onChange={handleChange}>
            {yearList.map((item, index) => (
              <StyledToggleButton key={index} value={item}>
                {item}
              </StyledToggleButton>
            ))}
          </ToggleButtonGroup>
        </AvailableProductsHeaderButtons>
      </AvailableProductsHeader>
      <DataTable displayToolbar={false} displayPagination={false} lineHover columns={columns} data={tableData || []} loading={loading} error={error} />
    </>
  );
};

AvailableProducts.propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(Object.keys(commodities)).isRequired,
  years: PropTypes.object,
};

export default AvailableProducts;
